import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventBusyIcon from '@mui/icons-material/EventBusy';

const WorkOrderTimeProgressBar = ({ createdAt, dueDate, status }) => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());

  const isCompleted = status === 'completed' || status === 'cancelled';
  const isOverdueStatus = status === 'overdue';

  const getTimeRemaining = () => {
    if (!dueDate) return { text: 'No due date', percentage: 0, overdue: false };

    const due = new Date(dueDate);
    const now = isCompleted ? new Date(dueDate) : new Date(); // If completed, show 100%
    const totalMs = due - new Date(createdAt);
    const elapsedMs = now - new Date(createdAt);

    const percentage = Math.min((elapsedMs / totalMs) * 100, 100);
    const remainingMs = due - now;
    const overdue = remainingMs < 0 || isOverdueStatus;

    if (status === 'completed') {
      return { text: `Completed`, percentage: 100, overdue: false };
    }

    if (status === 'cancelled') {
      return { text: `Cancelled`, percentage: 0, overdue: false };
    }

    // If status is 'overdue', always show as overdue with time calculation
    if (isOverdueStatus || overdue) {
      const overdueMs = Math.abs(remainingMs);
      const days = Math.floor(overdueMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((overdueMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return { text: `Overdue by ${days}d ${hours}h`, percentage: 100, overdue: true };
    }

    const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return { text: `${days}d ${hours}h remaining`, percentage, overdue: false };
  };

  const { text, percentage, overdue } = getTimeRemaining();

  useEffect(() => {
    if (!isCompleted) {
      const timer = setInterval(() => setCurrentTime(new Date()), 60000); // Update every minute
      return () => clearInterval(timer);
    }
  }, [isCompleted]);

  if (!dueDate) {
    return (
      <Typography variant="caption" color="text.secondary">
        No due date set
      </Typography>
    );
  }

  let progressColor = theme.palette.success.main;
  if (status === 'completed') {
    progressColor = theme.palette.success.main;
  } else if (status === 'cancelled') {
    progressColor = theme.palette.grey[400];
  } else if (status === 'overdue' || overdue) {
    progressColor = theme.palette.error.main;
  } else if (percentage > 80) {
    progressColor = theme.palette.warning.main;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {text}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          {new Date(dueDate).toLocaleDateString('en-GB')}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.grey[300], 0.4),
          '& .MuiLinearProgress-bar': {
            backgroundColor: progressColor,
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
};

export default WorkOrderTimeProgressBar;